// src/lib/api_travel.ts
// Cliente API contra el Django backend de la agencia de viajes.
// Usa /django-api/ como proxy (vite en dev, Traefik en prod) para evitar
// ERR_CERT_AUTHORITY_INVALID con el cert auto-firmado de Traefik.
// Autenticación: usa el JWT de Orion (auth_token) directamente.

// Absolute path — relative ('django-api') would resolve against the
// current location, which under react-router can be deep (`/m/x/y`)
// and break the fetch. Must match the proxy key in vite.config.ts.
const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

/* ─── Auth: reusa el JWT del login de Orion ─── */
async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

/** Headers con Bearer JWT */
async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

/** Fetch genérico contra Django (via proxy) */
/** Custom error with HTTP status for granular UX handling */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const url = `${DJANGO_PROXY}${path}`;
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada. Inicia sesión nuevamente.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos para esta acción.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado o no pertenece a tu organización.', 404);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación. Revisa los campos.', 422);
    if (res.status === 502) throw new ApiError('Dependencia externa no disponible. Intenta de nuevo.', 502);
    throw new ApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/* ═══════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════ */

export interface Paginated<T> { items: T[]; count: number }

export interface Capability {
  id: string;
  scope: string;
  kind: 'resource' | 'tool';
  name: string;
  method: string;
  path: string;
  summary: string;
  description: string;
  parameters?: { name: string; in: string; type: string; required: boolean }[];
}

export interface PermissionsResponse {
  key_name: string;
  scopes: string[];
  expires_in_seconds: number;
  capabilities: Capability[];
}

export interface Trip {
  id: number;
  name: string;
  slug: string;
  long_title: string;
  subtitle: string;
  slogan: string;
  location: string;
  short_description: string;
  duration: number;
  duration_unit: string;
  min_participants: number;
  max_participants: number;
  min_age: number;
  max_age: number;
  pets_allowed: boolean;
  physical_level: number;
  wheelchair_accessible: boolean;
  active: boolean;
  /** Anticipo POR VIAJE. null/vacío = usa la política de la empresa.
   *  El backend normaliza "15%", 0.15 → 15 y "$3,000" → 3000. */
  deposit_percentage?: number | string | null;
  deposit_min_amount?: number | string | null;
  deposit_per_person?: number | string | null;
  created_at: string;
  updated_at: string;
  /** Solo lectura, para las tarjetas del panel (mcp_django #489). Mismo cálculo que el sitio. */
  cover_image?: string | null;
  starting_price?: string | null;
  plans_count?: number;
}

/* ── Trip sub-resources ── */

export interface TripOverview {
  concept: string;
  story: string;
}

export interface ItineraryItem {
  id: number;
  day: number;
  time?: string;        // hora opcional "HH:MM" ('' = sin hora)
  title: string;
  description: string;
  type_activity: string;
  order?: number;       // orden de la actividad dentro del día
  active: boolean;
}

export interface FaqItem {
  id: number;
  question: string;
  answer: string;
  order: number;
}

export interface TripDate {
  id: number;
  date: string; // YYYY-MM-DD
  active: boolean;
}

export interface GoalItem {
  id: number;
  text: string;
  order: number;
  active: boolean;
}

export interface InclusionItem {
  id: number;
  inclusion: string;
  included: boolean;
}

/* ── Media ── */

export interface MediaItem {
  id: number;
  slug: string;
  alt_text: string;
  filename: string;
  original_filename: string;
  file: string; // CDN URL
  media_type_id: number;
  media_type: 'image' | 'video';
  description: string;
  width: number | null;
  height: number | null;
  created_at: string;
  updated_at: string;
}

export interface SlotStatus {
  id: number;
  name: string;
  description: string;
  max_items: number;
  current_count: number;
  allowed_media_types: string[];
  meta_data_schema: any | null;
}

export interface TripMediaLink {
  id: number;
  trip_id: number;
  media_id: number;
  slot_definition_id: number;
  meta_data: Record<string, any> | null;
  order: number;
  created_at: string;
  updated_at: string;
}

/** Item assigned to a slot (returned by GET /trips/{id}/media/slots/{slotId}) */
export interface SlotItem {
  id: number;            // assignment ID (use for removal)
  media_id: number;
  slug: string;
  filename: string;
  alt_text: string;
  file: string;          // CDN URL
  media_type: 'image' | 'video';
  width: number | null;
  height: number | null;
  meta_data: Record<string, any> | null;
  order: number;
  created_at: string;
}

/* ── Plan sub-resources ── */

export interface PlanPrice {
  id: number;
  person_type: string; // adult | child | pet
  price: string;       // decimal string
  currency: string;
  valid_from: string;
  valid_to: string;
}

export interface PlanFeature {
  id: number;
  text: string;
  order: number;
}

/* ── Addon sub-resources ── */

export interface AddonPrice {
  id: number;
  price: string;
  currency: string;
  valid_from: string;
  valid_to: string;
}

export interface TripAddon {
  id: number;
  trip_id: number;
  addon_id: number;
  active: boolean;
}

/* ── Company sub-resources ── */

export interface Department {
  id: number;
  name: string;
}

export interface SocialLink {
  id: number;
  platform: string;
  url: string;
}

export interface CompanyContact {
  id: number;
  department_id: number;
  type: string; // email | phone
  value: string;
}

/* ── Blog ── */

export interface BlogTag {
  id: number;
  name: string;
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Plan {
  id: number;
  trip_id: number;
  slug: string;
  name: string;
  description: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Addon {
  id: number;
  slug: string;
  name: string;
  description: string;
  category: string | null;
  available: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BlogPost {
  id: number;
  trip_id: number;
  author_id: number;
  image_id?: number | null;
  og_image_url?: string;
  meta_title?: string;
  meta_description?: string;
  title: string;
  abstract: string;
  content: string;
  status: string;
  active: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface Author {
  id: number;
  name: string;
  role: string | null;
  bio: string | null;
  active: boolean;
}

export interface Company {
  id?: number;
  name: string;
  slogan?: string;
  description: string;
  website: string;
  phone?: string;
  logo?: string; // CDN URL
  favicon?: string; // CDN URL — opcional, fallback al logo cuadrado
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  background_color?: string;
  text_color?: string;
  mensaje_bienvenida?: string;
  video_bienvenida_url?: string;
  reservation_policies?: string;
}

export interface EmailConfig {
  email_provider: string;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password?: string;
  smtp_from_email: string;
  smtp_use_ssl: boolean;
  smtp_use_tls: boolean;
  imap_host: string;
  imap_port: number;
  imap_ssl: boolean;
  imap_user: string;
  imap_password?: string;
  imap_mailbox: string;
  imap_poll_seconds: number;
}

export interface StripeConfig {
  stripe_secret_key?: string;
  stripe_secret_key_set: boolean;
  stripe_webhook_secret?: string;
  stripe_webhook_secret_set: boolean;
  stripe_success_url: string;
  stripe_cancel_url: string;
}

export interface WhatsAppConfig {
  mensaje_bienvenida: string;
  video_bienvenida_url: string;
}

export interface MetaConfig {
  meta_page_id: string;
  meta_ig_account_id: string;
  meta_ad_account_id: string;
  meta_waba_id: string;
  meta_wa_phone_number_id: string;
}

/* ═══════════════════════════════════════════════════════════════
   ENDPOINTS
   ═══════════════════════════════════════════════════════════════ */

/** Permisos y capabilities de la API key */
export function getPermissions(): Promise<PermissionsResponse> {
  return djangoFetch('/api/v1/admin/my-permissions');
}

/* ── Trips ── */
/** `incluirInactivos`: el panel de edición necesita ver los viajes apagados para poder
 *  reactivarlos (antes desaparecían). Un backend viejo ignora el parámetro: solo activos. */
export function listTrips(limit = 50, offset = 0, incluirInactivos = false): Promise<Paginated<Trip>> {
  const extra = incluirInactivos ? '&incluir_inactivos=true' : '';
  return djangoFetch(`/api/v1/admin/trips/?limit=${limit}&offset=${offset}${extra}`);
}
export function getTrip(id: number): Promise<Trip> {
  return djangoFetch(`/api/v1/admin/trips/${id}`);
}
export function createTrip(body: Partial<Trip>): Promise<Trip> {
  return djangoFetch('/api/v1/admin/trips/', { method: 'POST', body: JSON.stringify(body) });
}
export function updateTrip(id: number, body: Partial<Trip>): Promise<Trip> {
  return djangoFetch(`/api/v1/admin/trips/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteTrip(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/${id}`, { method: 'DELETE' });
}

/* ── Categories ── */
export function listCategories(limit = 50, offset = 0): Promise<Paginated<Category>> {
  return djangoFetch(`/api/v1/admin/trips/categories?limit=${limit}&offset=${offset}`);
}
export function getCategory(id: number): Promise<Category> {
  return djangoFetch(`/api/v1/admin/trips/categories/${id}`);
}
export function createCategory(body: Partial<Category>): Promise<Category> {
  return djangoFetch('/api/v1/admin/trips/categories', { method: 'POST', body: JSON.stringify(body) });
}
export function updateCategory(id: number, body: Partial<Category>): Promise<Category> {
  return djangoFetch(`/api/v1/admin/trips/categories/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteCategory(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/categories/${id}`, { method: 'DELETE' });
}

/* ── Plans ── */
export interface GalleryImg { id: number; url: string; prompt?: string }
/** Imágenes de la galería de la org (para elegir portada del blog sin URLs). */
export async function listGalleryImages(pageSize = 60): Promise<GalleryImg[]> {
  try {
    const r = await djangoFetch<any>(`/api/v1/admin/multimedia/gallery/?page_size=${pageSize}`);
    return (r?.items ?? r?.results ?? []) as GalleryImg[];
  } catch { return []; }
}

export function listPlans(tripId?: number): Promise<Paginated<Plan>> {
  const qs = tripId ? `?trip_id=${tripId}` : '';
  return djangoFetch(`/api/v1/admin/catalog/plans${qs}`);
}
export function getPlan(id: number): Promise<Plan> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${id}`);
}
export function createPlan(body: Partial<Plan> & { trip_id: number }): Promise<Plan> {
  return djangoFetch('/api/v1/admin/catalog/plans', { method: 'POST', body: JSON.stringify(body) });
}
export function updatePlan(id: number, body: Partial<Plan>): Promise<Plan> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function deletePlan(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${id}`, { method: 'DELETE' });
}

/* ── Addons ── */
export function listAddons(): Promise<Paginated<Addon>> {
  return djangoFetch('/api/v1/admin/catalog/addons');
}
export function getAddon(id: number): Promise<Addon> {
  return djangoFetch(`/api/v1/admin/catalog/addons/${id}`);
}
/** ⚠️ trip_id es OBLIGATORIO: el backend crea Y VINCULA en una transacción.
 *  (Antes se podía crear sin viaje → el addon quedaba huérfano e invisible.) */
export function createAddon(body: Partial<Addon> & { trip_id: number }): Promise<Addon> {
  return djangoFetch('/api/v1/admin/catalog/addons', { method: 'POST', body: JSON.stringify(body) });
}
export function updateAddon(id: number, body: Partial<Addon>): Promise<Addon> {
  return djangoFetch(`/api/v1/admin/catalog/addons/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteAddon(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/catalog/addons/${id}`, { method: 'DELETE' });
}

/* ── Blogs ── */
export function listBlogs(): Promise<Paginated<BlogPost>> {
  return djangoFetch('/api/v1/admin/blogs/');
}
export function getBlog(id: number): Promise<BlogPost> {
  return djangoFetch(`/api/v1/admin/blogs/${id}`);
}
export function createBlog(body: Partial<BlogPost>): Promise<BlogPost> {
  return djangoFetch('/api/v1/admin/blogs/', { method: 'POST', body: JSON.stringify(body) });
}
export function updateBlog(id: number, body: Partial<BlogPost>): Promise<BlogPost> {
  return djangoFetch(`/api/v1/admin/blogs/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteBlog(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/blogs/${id}`, { method: 'DELETE' });
}

/* ── Authors ── */
export function listAuthors(): Promise<Paginated<Author>> {
  return djangoFetch('/api/v1/admin/blogs/authors');
}
export function getAuthor(id: number): Promise<Author> {
  return djangoFetch(`/api/v1/admin/blogs/authors/${id}`);
}
export function createAuthor(body: Partial<Author>): Promise<Author> {
  return djangoFetch('/api/v1/admin/blogs/authors', { method: 'POST', body: JSON.stringify(body) });
}
export function updateAuthor(id: number, body: Partial<Author>): Promise<Author> {
  return djangoFetch(`/api/v1/admin/blogs/authors/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteAuthor(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/blogs/authors/${id}`, { method: 'DELETE' });
}

/* ── Company ── */
export function getCompany(): Promise<Company> {
  return djangoFetch('/api/v1/admin/company/');
}
export function updateCompany(body: Partial<Company>): Promise<Company> {
  return djangoFetch('/api/v1/admin/company/', { method: 'PUT', body: JSON.stringify(body) });
}
export async function uploadCompanyLogo(file: File): Promise<{ logo_url: string }> {
  const jwt = await getJwt();
  const compressed = await compressImage(file);
  const fd = new FormData();
  fd.append('file', compressed);
  const res = await fetch(`${DJANGO_PROXY}/api/v1/admin/company/logo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.detail || `Upload failed (${res.status})`, res.status);
  }
  return res.json();
}
export function deleteCompanyLogo(): Promise<void> {
  return djangoFetch('/api/v1/admin/company/logo', { method: 'DELETE' });
}
export async function uploadCompanyFavicon(file: File): Promise<Company> {
  const jwt = await getJwt();
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${DJANGO_PROXY}/api/v1/admin/company/favicon`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.detail || `Upload failed (${res.status})`, res.status);
  }
  return res.json();
}
export function deleteCompanyFavicon(): Promise<Company> {
  return djangoFetch('/api/v1/admin/company/favicon', { method: 'DELETE' });
}

/* ── Company Email Config ── */
export function getEmailConfig(): Promise<EmailConfig> {
  return djangoFetch('/api/v1/admin/company/email-config');
}
export function updateEmailConfig(body: Partial<EmailConfig>): Promise<EmailConfig> {
  return djangoFetch('/api/v1/admin/company/email-config', { method: 'PUT', body: JSON.stringify(body) });
}
export function testEmailConfig(toEmail: string): Promise<{ message: string }> {
  return djangoFetch('/api/v1/admin/company/email-config/test', { method: 'POST', body: JSON.stringify({ to_email: toEmail }) });
}

/* ── Company Stripe Config ── */
export function getStripeConfig(): Promise<StripeConfig> {
  return djangoFetch('/api/v1/admin/company/stripe-config');
}
export function updateStripeConfig(body: Partial<StripeConfig>): Promise<StripeConfig> {
  return djangoFetch('/api/v1/admin/company/stripe-config', { method: 'PUT', body: JSON.stringify(body) });
}

/* ── Company WhatsApp Config ── */
export function getWhatsAppConfig(): Promise<WhatsAppConfig> {
  return djangoFetch('/api/v1/admin/company/whatsapp-config');
}
export function updateWhatsAppConfig(body: Partial<WhatsAppConfig>): Promise<WhatsAppConfig> {
  return djangoFetch('/api/v1/admin/company/whatsapp-config', { method: 'PUT', body: JSON.stringify(body) });
}

/* ── Company Meta Config ── */
export function getMetaConfig(): Promise<MetaConfig> {
  return djangoFetch('/api/v1/admin/company/meta-config');
}
export function updateMetaConfig(body: Partial<MetaConfig>): Promise<MetaConfig> {
  return djangoFetch('/api/v1/admin/company/meta-config', { method: 'PUT', body: JSON.stringify(body) });
}

/* ── Company Third-party API keys (H5) ── */
export interface ThirdPartyKeys {
  heygen_api_key_set: boolean;
  elevenlabs_api_key_set: boolean;
  fal_api_key_set: boolean;
  remotion_renderer_url: string;
  remotion_api_token_set: boolean;
}
export interface ThirdPartyKeysUpdate {
  heygen_api_key?: string | null;
  elevenlabs_api_key?: string | null;
  fal_api_key?: string | null;
  remotion_renderer_url?: string | null;
  remotion_api_token?: string | null;
}
export function getThirdPartyKeys(): Promise<ThirdPartyKeys> {
  return djangoFetch('/api/v1/admin/company/third-party-keys');
}
export function updateThirdPartyKeys(body: ThirdPartyKeysUpdate): Promise<ThirdPartyKeys> {
  return djangoFetch('/api/v1/admin/company/third-party-keys', {
    method: 'PUT', body: JSON.stringify(body),
  });
}

/* ═══════════════════════════════════════════════════════════════
   SUB-RESOURCE ENDPOINTS
   ═══════════════════════════════════════════════════════════════ */

/* ── Trip Overview ── */
export function getTripOverview(tripId: number): Promise<TripOverview> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/overview`);
}
export function updateTripOverview(tripId: number, body: TripOverview): Promise<TripOverview> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/overview`, { method: 'PUT', body: JSON.stringify(body) });
}

/* ── Trip Itinerary ── */
export function listItinerary(tripId: number): Promise<ItineraryItem[]> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/itinerary`);
}
export function createItineraryItem(tripId: number, body: Partial<ItineraryItem>): Promise<ItineraryItem> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/itinerary`, { method: 'POST', body: JSON.stringify(body) });
}
/** PATCH parcial: corrige un día del itinerario SIN borrarlo y recrearlo. */
export function updateItineraryItem(
  tripId: number,
  itemId: number,
  body: Partial<ItineraryItem>,
): Promise<ItineraryItem> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/itinerary/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}
export function deleteItineraryItem(tripId: number, itemId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/itinerary/${itemId}`, { method: 'DELETE' });
}

/* ── Trip FAQs ── */
export function listFaqs(tripId: number): Promise<FaqItem[]> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/faqs`);
}
export function createFaq(tripId: number, body: Partial<FaqItem>): Promise<FaqItem> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/faqs`, { method: 'POST', body: JSON.stringify(body) });
}
export function deleteFaq(tripId: number, faqId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/faqs/${faqId}`, { method: 'DELETE' });
}

/* ── Trip Dates ── */
export function listTripDates(tripId: number): Promise<TripDate[]> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/dates`);
}
export function createTripDate(tripId: number, body: Partial<TripDate>): Promise<TripDate> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/dates`, { method: 'POST', body: JSON.stringify(body) });
}
export function deleteTripDate(tripId: number, dateId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/dates/${dateId}`, { method: 'DELETE' });
}

/* ── Trip Goals ── */
export function listGoals(tripId: number): Promise<GoalItem[]> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/goals`);
}
export function createGoal(tripId: number, body: Partial<GoalItem>): Promise<GoalItem> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/goals`, { method: 'POST', body: JSON.stringify(body) });
}
export function deleteGoal(tripId: number, goalId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/goals/${goalId}`, { method: 'DELETE' });
}

/* ── Trip Inclusions ── */
export function listInclusions(tripId: number): Promise<InclusionItem[]> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/inclusions`);
}
export function createInclusion(tripId: number, body: Partial<InclusionItem>): Promise<InclusionItem> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/inclusions`, { method: 'POST', body: JSON.stringify(body) });
}
export function deleteInclusion(tripId: number, inclusionId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/inclusions/${inclusionId}`, { method: 'DELETE' });
}

/* ── Trip Media Library ── */
export async function listTripMedia(tripId: number): Promise<MediaItem[]> {
  const data = await djangoFetch(`/api/v1/admin/trips/${tripId}/media/library`);
  // API returns { items: [...], count: N }
  if (data && typeof data === 'object' && 'items' in data) return (data as any).items;
  if (Array.isArray(data)) return data;
  return [];
}
/* ── Client-side image compression (canvas resize) ── */
const COMPRESS_MAX_PX   = 2048;   // max width or height
const COMPRESS_QUALITY  = 0.82;   // JPEG/WebP quality
const COMPRESS_THRESHOLD = 3 * 1024 * 1024; // only compress if > 3 MB

export function compressImage(file: File, maxPx = COMPRESS_MAX_PX, quality = COMPRESS_QUALITY): Promise<File> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/') || file.size <= COMPRESS_THRESHOLD) {
      return resolve(file); // skip non-images and small files
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= maxPx && height <= maxPx) return resolve(file); // already small
      const ratio = Math.min(maxPx / width, maxPx / height);
      width  = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(file);
      ctx.drawImage(img, 0, 0, width, height);
      const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      canvas.toBlob(
        blob => {
          if (!blob) return resolve(file);
          const compressed = new File([blob], file.name, { type: mime, lastModified: Date.now() });
          resolve(compressed);
        },
        mime,
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen para comprimir')); };
    img.src = url;
  });
}

export async function uploadTripMedia(
  tripId: number,
  files: File | File[],
  slotId?: number,
  metaDatas?: Record<string, any>[],
): Promise<{ uploaded: MediaItem[]; errors: any[]; assigned: any[] }> {
  const jwt = await getJwt();
  const list = Array.isArray(files) ? files : [files];
  // Compress images client-side before uploading
  const compressed = await Promise.all(list.map(f => compressImage(f)));
  const fd = new FormData();
  compressed.forEach(f => fd.append('files', f));
  if (metaDatas) fd.append('meta_datas', JSON.stringify(metaDatas));
  const uploadPath = slotId != null
    ? `/api/v1/admin/trips/${tripId}/media/slots/${slotId}/upload`
    : `/api/v1/admin/trips/${tripId}/media/upload`;
  const res = await fetch(`${DJANGO_PROXY}${uploadPath}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: fd,
  });
  if (res.status === 413) {
    throw new ApiError(
      'El archivo es demasiado grande. El límite del servidor es ~10 MB por archivo. Intenta con una imagen más pequeña.',
      413,
    );
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = body.detail || body.errors?.map((e: any) => `${e.filename}: ${JSON.stringify(e.errors)}`).join('; ') || `Upload failed (${res.status})`;
    throw new ApiError(detail, res.status);
  }
  // API returns { uploaded: [...], errors: [...], assigned: [...] }
  if (body.errors?.length) {
    const msgs = body.errors.map((e: any) => `${e.filename}: ${JSON.stringify(e.errors)}`).join('; ');
    throw new Error(msgs);
  }
  return body;
}
export function deleteTripMedia(tripId: number, mediaId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/media/library/${mediaId}`, { method: 'DELETE' });
}

/* ── Trip Media Slots ── */
export function listTripSlots(tripId: number): Promise<SlotStatus[]> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/media/slots`);
}
export async function getSlotItems(tripId: number, slotId: number): Promise<SlotItem[]> {
  const data = await djangoFetch(`/api/v1/admin/trips/${tripId}/media/slots/${slotId}`);
  return Array.isArray(data) ? data : [];
}
export function assignMediaToSlot(
  tripId: number,
  slotId: number,
  body: { media_id: number; order?: number; meta_data?: Record<string, any> },
): Promise<TripMediaLink> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/media/slots/${slotId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export function removeMediaFromSlot(tripId: number, slotId: number, linkId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/trips/${tripId}/media/slots/${slotId}/${linkId}`, { method: 'DELETE' });
}

/* ── Plan Prices ── */
export function listPlanPrices(planId: number): Promise<PlanPrice[]> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${planId}/prices`);
}
export function createPlanPrice(planId: number, body: Partial<PlanPrice>): Promise<PlanPrice> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${planId}/prices`, { method: 'POST', body: JSON.stringify(body) });
}
export function deletePlanPrice(planId: number, priceId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${planId}/prices/${priceId}`, { method: 'DELETE' });
}

/* ── Plan Features ── */
export function listPlanFeatures(planId: number): Promise<PlanFeature[]> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${planId}/features`);
}
export function createPlanFeature(planId: number, body: Partial<PlanFeature>): Promise<PlanFeature> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${planId}/features`, { method: 'POST', body: JSON.stringify(body) });
}
export function deletePlanFeature(planId: number, featureId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/catalog/plans/${planId}/features/${featureId}`, { method: 'DELETE' });
}

/* ── Addon Prices ── */
export function listAddonPrices(addonId: number): Promise<AddonPrice[]> {
  return djangoFetch(`/api/v1/admin/catalog/addons/${addonId}/prices`);
}
export function createAddonPrice(addonId: number, body: Partial<AddonPrice>): Promise<AddonPrice> {
  return djangoFetch(`/api/v1/admin/catalog/addons/${addonId}/prices`, { method: 'POST', body: JSON.stringify(body) });
}
export function deleteAddonPrice(addonId: number, priceId: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/catalog/addons/${addonId}/prices/${priceId}`, { method: 'DELETE' });
}

/* ── Trip-Addons ── */
export function listTripAddons(): Promise<Paginated<TripAddon>> {
  return djangoFetch('/api/v1/admin/catalog/trip-addons');
}
export function createTripAddon(body: { trip_id: number; addon_id: number; active?: boolean }): Promise<TripAddon> {
  return djangoFetch('/api/v1/admin/catalog/trip-addons', { method: 'POST', body: JSON.stringify(body) });
}
export function deleteTripAddon(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/catalog/trip-addons/${id}`, { method: 'DELETE' });
}

/* ── Company Departments ── */
export function listDepartments(): Promise<Department[]> {
  return djangoFetch('/api/v1/admin/company/departments');
}
export function createDepartment(body: Partial<Department>): Promise<Department> {
  return djangoFetch('/api/v1/admin/company/departments', { method: 'POST', body: JSON.stringify(body) });
}
export function deleteDepartment(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/company/departments/${id}`, { method: 'DELETE' });
}

/* ── Company Social Links ── */
export function listSocialLinks(): Promise<SocialLink[]> {
  return djangoFetch('/api/v1/admin/company/social-links');
}
export function createSocialLink(body: Partial<SocialLink>): Promise<SocialLink> {
  return djangoFetch('/api/v1/admin/company/social-links', { method: 'POST', body: JSON.stringify(body) });
}
export function deleteSocialLink(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/company/social-links/${id}`, { method: 'DELETE' });
}

/* ── Company Contacts ── */
export function listContacts(): Promise<CompanyContact[]> {
  return djangoFetch('/api/v1/admin/company/contacts');
}
export function createContact(body: Partial<CompanyContact>): Promise<CompanyContact> {
  return djangoFetch('/api/v1/admin/company/contacts', { method: 'POST', body: JSON.stringify(body) });
}
export function deleteContact(id: number): Promise<void> {
  return djangoFetch(`/api/v1/admin/company/contacts/${id}`, { method: 'DELETE' });
}

/* ── Blog Tags ── */
export function listBlogTags(): Promise<Paginated<BlogTag>> {
  return djangoFetch('/api/v1/admin/blogs/tags');
}
export function createBlogTag(body: { name: string }): Promise<BlogTag> {
  return djangoFetch('/api/v1/admin/blogs/tags', { method: 'POST', body: JSON.stringify(body) });
}

/* ═══════════════════════════════════════════════════════════════
   BOOKINGS MODULE
   ═══════════════════════════════════════════════════════════════ */

/* ── Types ── */

export interface BookingKpis {
  total_bookings: number;
  paid_bookings: number;
  pending_bookings: number;
  cancelled_bookings: number;
  refunded_bookings: number;
  total_revenue: string;
  average_ticket: string;
  unique_customers: number;
  total_passengers: number;
  active_trips: number;
  upcoming_trips: number;
}

export interface StatusBreakdown {
  status: string;
  booking_count: number;
  revenue: string;
}

export interface RevenueSeries {
  date: string;
  booking_count: number;
  paid_booking_count: number;
  revenue: string;
}

export interface TopTrip {
  trip_id: number;
  trip_name: string;
  trip_slug: string;
  booking_count: number;
  paid_booking_count: number;
  total_passengers: number;
  revenue: string;
  next_date: string | null;
}

export interface UpcomingTrip {
  trip_id: number;
  trip_name: string;
  trip_slug: string;
  next_date: string;
  booking_count: number;
  paid_count: number;
}

export interface BookingDashboard {
  period: { start_date: string; end_date: string };
  kpis: BookingKpis;
  status_breakdown: StatusBreakdown[];
  revenue_series: RevenueSeries[];
  top_trips: TopTrip[];
  upcoming_trips: UpcomingTrip[];
}

export interface BookingListItem {
  id: number;
  status: string;
  trip_name: string;
  plan_name: string;
  date_trip: string;
  customer_name: string;
  customer_email: string;
  total: string;
  currency: string;
  paid_at: string | null;
  created_at: string;
}

export interface BookingPerson {
  person_type: string;
  label: string;
  quantity: number;
  unit_price: string;
  subtotal: string;
}

export interface BookingAddon {
  trip_addon_id: number;
  addon_name: string;
  category: string;
  quantity: number;
  quantity_included: number;
  quantity_to_pay: number;
  unit_price: string;
  subtotal: string;
  included_in_plan: boolean;
}

export interface BookingPayment {
  id: number;
  payment_type: string;
  amount: string;
  currency: string;
  description: string;
  status: string;
  checkout_url: string;
  paid_at: string | null;
  created_at: string;
}

export interface BookingDetail extends BookingListItem {
  payment_status: string;
  customer_phone: string;
  subtotal_persons: string;
  subtotal_addons: string;
  amount_paid: string;
  amount_due: string;
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  confirmation_email_sent: boolean;
  expires_at: string | null;
  persons: BookingPerson[];
  addons: BookingAddon[];
  payments: BookingPayment[];
  metadata: Record<string, any>;
  updated_at: string;
}

export interface BookingAccountResponse {
  booking_id: number;
  trip: string;
  plan: string;
  date: string;
  persons: BookingPerson[];
  total: string;
  amount_paid: string;
  amount_due: string;
  payment_status: string;
  payments: BookingPayment[];
  addons: BookingAddon[];
  cross_sell: CrossSellItem[];
}

export interface CrossSellItem {
  trip_addon_id: number;
  addon_name: string;
  price: number;
  available: boolean;
}

export interface CreatePaymentRequest {
  payment_type?: string;
  amount?: number | null;
  description?: string;
}

export interface CreatePaymentResponse {
  payment_id: number;
  checkout_url: string;
  amount: string;
  booking_id: number;
  amount_due_after: string;
}

export interface AddBookingAddonResponse {
  addon: string;
  amount: string;
  payment_id: number;
  checkout_url: string;
  new_total: string;
  new_amount_due: string;
}

export interface OrionConfig {
  orion_api_url: string;
  jwt_shared: boolean;
}

/* ── Endpoints ── */

export function getBookingDashboard(params?: {
  start_date?: string; end_date?: string; top_limit?: number;
}): Promise<BookingDashboard> {
  const qs = new URLSearchParams();
  if (params?.start_date) qs.set('start_date', params.start_date);
  if (params?.end_date) qs.set('end_date', params.end_date);
  if (params?.top_limit) qs.set('top_limit', String(params.top_limit));
  const q = qs.toString();
  return djangoFetch(`/api/v1/admin/bookings/dashboard${q ? `?${q}` : ''}`);
}

export function listBookings(params?: {
  status?: string; trip_id?: number; search?: string; offset?: number; limit?: number;
}): Promise<Paginated<BookingListItem>> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.trip_id) qs.set('trip_id', String(params.trip_id));
  if (params?.search) qs.set('search', params.search);
  if (params?.offset != null) qs.set('offset', String(params.offset));
  if (params?.limit != null) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return djangoFetch(`/api/v1/admin/bookings/${q ? `?${q}` : ''}`);
}

export function getBooking(id: number): Promise<BookingDetail> {
  return djangoFetch(`/api/v1/admin/bookings/${id}`);
}

export function cancelBooking(id: number): Promise<{ message: string; status: string }> {
  return djangoFetch(`/api/v1/admin/bookings/${id}/cancel`, { method: 'POST' });
}

export function refundBooking(id: number): Promise<{ message: string; status: string }> {
  return djangoFetch(`/api/v1/admin/bookings/${id}/refund`, { method: 'POST' });
}

export function resendBookingEmail(id: number): Promise<{ message: string }> {
  return djangoFetch(`/api/v1/admin/bookings/${id}/resend-email`, { method: 'POST' });
}

export function listUpcomingTrips(): Promise<UpcomingTrip[]> {
  return djangoFetch('/api/v1/admin/bookings/upcoming');
}

export function listBookingsByTrip(tripId: number, params?: { offset?: number; limit?: number }): Promise<Paginated<BookingListItem>> {
  const qs = new URLSearchParams();
  if (params?.offset != null) qs.set('offset', String(params.offset));
  if (params?.limit != null) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return djangoFetch(`/api/v1/admin/bookings/by-trip/${tripId}${q ? `?${q}` : ''}`);
}

export function listBookingsByPhone(phone: string): Promise<Paginated<BookingListItem>> {
  return djangoFetch(`/api/v1/admin/bookings/by-phone/${encodeURIComponent(phone)}`);
}

export function getBookingAccount(bookingId: number): Promise<BookingAccountResponse> {
  return djangoFetch(`/api/v1/admin/bookings/${bookingId}/account`);
}

/** Alta MANUAL de reserva (venta por WhatsApp/mostrador, sin Stripe).
 *  El backend toma los precios del catálogo y crea la fecha del viaje si no
 *  existe (agencias "a demanda" no cargan fechas). */
export function createBookingManual(body: {
  trip_id: number;
  plan_id: number;
  date_trip: string;              // YYYY-MM-DD
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  persons: { person_type: string; quantity: number; unit_price?: number }[];
  amount_paid?: number;
  notes?: string;
  /** Solo admin. Si la registra una vendedora, el servidor la pone a su nombre. */
  seller_user_id?: string;
}): Promise<BookingDetail> {
  return djangoFetch('/api/v1/admin/bookings/', { method: 'POST', body: JSON.stringify(body) });
}

export function createBookingPayment(bookingId: number, body: CreatePaymentRequest): Promise<CreatePaymentResponse> {
  return djangoFetch(`/api/v1/admin/bookings/${bookingId}/payments`, { method: 'POST', body: JSON.stringify(body) });
}

export function addBookingAddon(bookingId: number, body: { trip_addon_id: number; quantity: number }): Promise<AddBookingAddonResponse> {
  return djangoFetch(`/api/v1/admin/bookings/${bookingId}/addons`, { method: 'POST', body: JSON.stringify(body) });
}

/* ── Orion Config ── */
export function getOrionConfig(): Promise<OrionConfig> {
  return djangoFetch('/api/v1/admin/company/orion-config');
}

/** Invalidar cache JWT (para re-login) */
export function clearJwtCache() {
  _cachedJwt = null;
  _jwtExpiresAt = 0;
}
